import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type {
    OrderGroupId} from "@@/shared/order-groups/order-group-id";
import {
    orderGroupIdZodSchema,
} from "@@/shared/order-groups/order-group-id";
import type {
    OrderEventId} from "@@/shared/orders/order-events/events/_order-event-id";
import {
    orderEventIdZodSchema,
} from "@@/shared/orders/order-events/events/_order-event-id";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";

type SetOnPremisePaymentFulfilledCommand = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "SET_ONPREMISE_PAYMENT_FULFILLED_COMMAND";
    /**
     * Order group to create payment intent for
     * @type {boolean}
     */
    readonly orderGroupId: OrderGroupId;
    readonly changeEventId?: OrderEventId;
};

type SetOnPremisePaymentFulfilledCommandResponse = {
    readonly _id: string;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "SET_ONPREMISE_PAYMENT_FULFILLED_COMMAND_RESPONSE";

    /**
     * Order group payment was created for
     * @type {boolean}
     */
    readonly orderGroupId: OrderGroupId;
};

type FactoryParams = Except<
    SetOnPremisePaymentFulfilledCommand,
    "_id" | "_type" | "_createdAt"
>;
type ResponseFactoryPArams = Except<
    SetOnPremisePaymentFulfilledCommandResponse,
    "_id" | "_type" | "_createdAt"
>;
const setOnPremisePaymentFulfilledCommandFactory = (params: FactoryParams) => {
    const setOnPremisePaymentFulfilledCommand: SetOnPremisePaymentFulfilledCommand =
        {
            ...params,
            _id: generateId({
                prefix: "set_onpremise_payment_fulfilled_command__",
            }),
            _type: "SET_ONPREMISE_PAYMENT_FULFILLED_COMMAND",
            _createdAt: nowIsoAndUnix(),
        };
    return setOnPremisePaymentFulfilledCommand;
};
const setOnPremisePaymentFulfilledCommandResponseFactory = (
    params: ResponseFactoryPArams,
) => {
    const setOnPremisePaymentFulfilledCommandResponse: SetOnPremisePaymentFulfilledCommandResponse =
        {
            ...params,
            _id: generateId({
                prefix: "set_onpremise_payment_fulfilled_command_response__",
            }),
            _type: "SET_ONPREMISE_PAYMENT_FULFILLED_COMMAND_RESPONSE",
            _createdAt: nowIsoAndUnix(),
        };
    return setOnPremisePaymentFulfilledCommandResponse;
};

const setOnPremisePaymentFulfilledCommandZodSchema: z.ZodSchema<SetOnPremisePaymentFulfilledCommand> =
    z.object({
        _id: idZodSchema,
        _createdAt: isoAndUnixZodSchema,
        _type: z.literal("SET_ONPREMISE_PAYMENT_FULFILLED_COMMAND"),
        orderGroupId: orderGroupIdZodSchema,
        changeEventId: orderEventIdZodSchema.optional(),
    });
export {
    setOnPremisePaymentFulfilledCommandFactory,
    setOnPremisePaymentFulfilledCommandResponseFactory,
    setOnPremisePaymentFulfilledCommandZodSchema,
};
export type {
    SetOnPremisePaymentFulfilledCommand,
    SetOnPremisePaymentFulfilledCommandResponse,
};
